// Local translations
export const T = {
  en_GB: {
    settings: "Settings",
    riskModels: "Risk Models",
    active: "Active",
    inactive: "Inactive",
    duplicate: "Duplicate",
    documentation: "Documentation",
    timeHorizons: "Time Horizons",
    maxFive: "Max 5",
    cancel: "Cancel",
    addTimeHorizonPlaceholder: "e.g., Short Term (1-2 years)",
    addTimeHorizon: "Add Time Horizon",
    editTimeHorizon: "Edit Time Horizon",
    riskLevels: "Risk Levels",
    maxRiskLevels: "Max 5",
    addRiskLevelPlaceholder: "e.g., Conservative",
    addRiskLevel: "Add Risk Level",
    editRiskLevel: "Edit Risk Level",
    matrixHint: "Add at least one time horizon and one risk level to configure the scoring matrix",
    deleteItemTitle: "Delete Item",
    deleteItemDescription:
      "Are you sure you want to delete this item? All associated matrix scores will also be deleted.",
    delete: "Delete",
    error: "Error",
    failedToAddTimeHorizon: "Failed to add time horizon",
    failedToUpdateTimeHorizon: "Failed to update time horizon",
    failedToDeleteTimeHorizon: "Failed to delete time horizon",
    failedToAddRiskLevel: "Failed to add risk level",
    failedToUpdateRiskLevel: "Failed to update risk level",
    failedToDeleteRiskLevel: "Failed to delete risk level",
    failedToUpdateModel: "Failed to update risk model",
    loading: "Loading...",
    notFound: "Risk model not found",
    // QP-187 — detail page tabs
    tabModel: "Model",
    tabQuestions: "Questions",
    tabSettings: "Settings",
    // QP-180 — display settings + cap
    displaySettings: "Display",
    displayTypeLabel: "Display type",
    displayDropdown: "List",
    displayCards: "Cards",
    displayTypeHint:
      "List shows the options as a simple list. Cards render each option as a selectable card with an icon and description.",
    capSettings: "Score cap",
    capFieldLabel: "Cap client field",
    capLevelLabel: "Cap level",
    capNone: "No cap",
    capValuesLabel: "Cap values",
    selectValues: "Select values",
    capValuesRequired: "Select at least one value that triggers the cap",
    capHint:
      "When the selected client field has one of the selected values, the final risk score is capped at the cap level.",
    // QL-212 — who each risk-assessment input belongs to
    scopeSettings: "Input scope",
    scopeHint:
      "Choose the scope of each risk-assessment input. Global: the client answers it once for the whole flow. Goal: the client answers it separately for every goal. Each goal still gets its own risk score.",
    scopeGoal: "Goal",
    scopeGlobal: "Global",
    scopeTimeHorizonLabel: "Time horizon scope",
    scopeTimeHorizonHint:
      "Global: one time horizon for the whole flow. Goal (default): each goal has its own.",
    scopeRiskLevelLabel: "Risk tolerance scope",
    scopeRiskLevelHint:
      "Global: risk tolerance is stated once for the whole flow. Goal (default): once per goal.",
    scopeAdjustmentQuestionsLabel: "Knowledge and experience questions scope",
    scopeAdjustmentQuestionsHint:
      "Global: answered once for the whole flow. Goal (default): once per goal. The questions are always scoped as a whole set.",
    scopeLocked:
      "Input scope is locked while the risk model is active, so answers already captured stay readable against the scope they were given under. Deactivate the model to change it, or duplicate it.",
    // Value-picker dialog
    selectTitle: (title: string) => `Select ${title}`,
    selectDescription: (count: string) =>
      `Choose the values that trigger the cap. ${count} selected.`,
    // QL-200 — display labels for boolean cap values (stored as "true"/"false")
    yes: "Yes",
    no: "No",
    // QL-144 — unsaved-questions tab guard
    unsavedChangesTitle: "Unsaved changes",
    unsavedChangesDescription:
      "The Questions tab has unsaved changes. Leaving the tab will discard them.",
    discardAndLeave: "Discard changes",
    keepEditing: "Keep editing",
  },
  nb_NO: {
    settings: "Innstillinger",
    riskModels: "Risikomodeller",
    active: "Aktiv",
    inactive: "Inaktiv",
    duplicate: "Dupliser",
    documentation: "Dokumentasjon",
    timeHorizons: "Tidshorisonter",
    maxFive: "Maks 5",
    cancel: "Avbryt",
    addTimeHorizonPlaceholder: "f.eks. Kort sikt (1-2 år)",
    addTimeHorizon: "Legg til tidshorisont",
    editTimeHorizon: "Rediger tidshorisont",
    riskLevels: "Risikonivåer",
    maxRiskLevels: "Maks 5",
    addRiskLevelPlaceholder: "f.eks. Konservativ",
    addRiskLevel: "Legg til risikonivå",
    editRiskLevel: "Rediger risikonivå",
    matrixHint: "Legg til minst én tidshorisont og ett risikonivå for å konfigurere poengmatrisen",
    deleteItemTitle: "Slett element",
    deleteItemDescription:
      "Er du sikker på at du vil slette dette elementet? Alle tilknyttede matriseverdier vil også bli slettet.",
    delete: "Slett",
    error: "Feil",
    failedToAddTimeHorizon: "Kunne ikke legge til tidshorisont",
    failedToUpdateTimeHorizon: "Kunne ikke oppdatere tidshorisont",
    failedToDeleteTimeHorizon: "Kunne ikke slette tidshorisont",
    failedToAddRiskLevel: "Kunne ikke legge til risikonivå",
    failedToUpdateRiskLevel: "Kunne ikke oppdatere risikonivå",
    failedToDeleteRiskLevel: "Kunne ikke slette risikonivå",
    failedToUpdateModel: "Kunne ikke oppdatere risikomodell",
    loading: "Laster...",
    notFound: "Risikomodell ikke funnet",
    // QP-187 — detail page tabs
    tabModel: "Modell",
    tabQuestions: "Spørsmål",
    tabSettings: "Innstillinger",
    // QP-180 — display settings + cap
    displaySettings: "Visning",
    displayTypeLabel: "Visningstype",
    displayDropdown: "Liste",
    displayCards: "Kort",
    displayTypeHint:
      "Liste viser alternativene som en enkel liste. Kort viser hvert alternativ som et valgbart kort med ikon og beskrivelse.",
    capSettings: "Poengtak",
    capFieldLabel: "Takfelt (klientfelt)",
    capLevelLabel: "Taknivå",
    capNone: "Ingen tak",
    capValuesLabel: "Takverdier",
    selectValues: "Velg verdier",
    capValuesRequired: "Velg minst én verdi som utløser taket",
    capHint:
      "Når det valgte klientfeltet har en av de valgte verdiene, begrenses den endelige risikoscoren til taknivået.",
    // QL-212 — hvem hver risikovurderingsinngang tilhører
    scopeSettings: "Omfang for inndata",
    scopeHint:
      "Velg omfang for hver risikovurderingsinngang. Global: kunden svarer én gang for hele flyten. Mål: kunden svarer separat for hvert mål. Hvert mål får fortsatt sin egen risikoscore.",
    scopeGoal: "Mål",
    scopeGlobal: "Global",
    scopeTimeHorizonLabel: "Omfang for tidshorisont",
    scopeTimeHorizonHint:
      "Global: én tidshorisont for hele flyten. Mål (standard): hvert mål har sin egen.",
    scopeRiskLevelLabel: "Omfang for risikotoleranse",
    scopeRiskLevelHint:
      "Global: risikotoleransen oppgis én gang for hele flyten. Mål (standard): én gang per mål.",
    scopeAdjustmentQuestionsLabel: "Omfang for kunnskaps- og erfaringsspørsmål",
    scopeAdjustmentQuestionsHint:
      "Global: besvares én gang for hele flyten. Mål (standard): én gang per mål. Spørsmålene har alltid omfang som én hel gruppe.",
    scopeLocked:
      "Omfang for inndata er låst mens risikomodellen er aktiv, slik at svar som allerede er registrert, fortsatt kan leses mot omfanget de ble gitt under. Deaktiver modellen for å endre det, eller dupliser den.",
    // Value-picker dialog
    selectTitle: (title: string) => `Velg ${title}`,
    selectDescription: (count: string) => `Velg verdiene som utløser taket. ${count} valgt.`,
    // QL-200 — display labels for boolean cap values (stored as "true"/"false")
    yes: "Ja",
    no: "Nei",
    // QL-144 — unsaved-questions tab guard
    unsavedChangesTitle: "Ulagrede endringer",
    unsavedChangesDescription:
      "Spørsmål-fanen har ulagrede endringer. Hvis du forlater fanen, forkastes de.",
    discardAndLeave: "Forkast endringer",
    keepEditing: "Fortsett å redigere",
  },
} as const;
